import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { SidebarNav } from './SidebarNav';
import { api } from '../lib/trpc/client';

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { circleId } = useParams<{ circleId: string }>();
  const [checkingCircle, setCheckingCircle] = useState(true);

  const hasSession = useMemo(() => {
    const accessToken = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userId');
    return Boolean(accessToken && userId);
  }, [location.pathname]);

  useEffect(() => {
    const run = async () => {
      try {
        if (!hasSession) {
          navigate('/', { replace: true });
          setCheckingCircle(false);
          return;
        }

        const hasIdentityKey = Boolean(localStorage.getItem('identitySkRaw'));
        const allowedWithoutIdentity = location.pathname === '/settings';
        if (!hasIdentityKey && !allowedWithoutIdentity) {
          navigate('/unlock', { replace: true });
          setCheckingCircle(false);
          return;
        }

        const allowedWithoutCircle = location.pathname === '/circles' || location.pathname === '/invites' || location.pathname === '/settings';
        if (allowedWithoutCircle) {
          setCheckingCircle(false);
          return;
        }

        // Validate URL circleId matches active circle or switch if needed
        const active = await api.circle.getActiveCircleId();
        if (circleId) {
           if (active.circleId !== circleId) {
              // Try to set active circle to match URL
              try {
                 await api.circle.setActiveCircle({ circleId });
              } catch {
                 // If failed to switch (not a member), redirect to circles list
                 navigate('/circles', { replace: true });
                 return;
              }
           }
        } else if (!active.circleId) {
          navigate('/circles', { replace: true });
        }
      } catch {
        // 401 handling is centralized in API client and will redirect.
      } finally {
        setCheckingCircle(false);
      }
    };

    void run();
  }, [hasSession, location.pathname, navigate, circleId]);

  if (checkingCircle) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto grid min-h-screen w-full max-w-7xl grid-cols-1 md:grid-cols-[220px_1fr]">
        <SidebarNav />
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
